import React from 'react';
import { Minus, Plus } from 'lucide-react';

interface NumberStepperProps {
  label: string;
  value?: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  helper?: string;
}

export const NumberStepper: React.FC<NumberStepperProps> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999999,
  step = 1,
  unit = '',
  helper,
}) => {
  const currentValue = typeof value === 'number' && !isNaN(value) ? value : min;

  const handleDecrement = () => {
    const next = Math.max(min, currentValue - step);
    onChange(next);
  };

  const handleIncrement = () => {
    const next = Math.min(max, currentValue + step);
    onChange(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFloat(e.target.value);
    if (!isNaN(parsed)) {
      onChange(Math.min(max, Math.max(min, parsed)));
    } else if (e.target.value === '') {
      onChange(min);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-300 tracking-wide uppercase">{label}</label>
        {unit && <span className="text-[10px] font-semibold text-slate-500">{unit}</span>}
      </div>

      <div className="flex items-center bg-slate-800 border border-slate-700/80 rounded-xl overflow-hidden shadow-xs focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={currentValue <= min}
          className="p-2.5 text-slate-400 hover:text-white hover:bg-slate-700/50 active:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>

        <input
          type="number"
          value={currentValue}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          className="flex-1 bg-transparent text-center text-xs font-black text-slate-100 py-2 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />

        <button
          type="button"
          onClick={handleIncrement}
          disabled={currentValue >= max}
          className="p-2.5 text-slate-400 hover:text-white hover:bg-slate-700/50 active:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {helper && <p className="text-[10px] text-slate-500">{helper}</p>}
    </div>
  );
};
